/**
 * Extracts the server's `{ error: string }` message from an RTK Query
 * error payload, falling back to a caller-supplied message when the shape
 * doesn't match (network failure, unexpected error body, etc). Shared by
 * every workforce view that surfaces mutation failures inline instead of
 * via a toast.
 *
 * #1433: a rejected Zod body arrives as `{ error: "Validation error",
 * details: flatten() }`, and reading only `error` showed the admin a bare
 * "Validation error" with no field and no reason. The field errors were in
 * the payload all along. `labels` maps body keys to the captions the form
 * actually shows, so the message names the field the admin is looking at
 * ("$/month") rather than the wire name ("monthlyCost").
 */
export function extractWorkforceError(
  error: unknown,
  fallback: string,
  labels: Record<string, string> = {},
): string {
  const data = (error as { data?: WorkforceErrorBody } | undefined)?.data;
  const message = data?.error;

  const fields = fieldMessages(data, labels);
  if (fields) return message === "Validation error" || !message ? fields : `${message} — ${fields}`;

  return message ?? fallback;
}

interface WorkforceErrorBody {
  error?: string;
  details?: {
    fieldErrors?: Record<string, string[] | undefined>;
    formErrors?: string[];
  };
}

/**
 * Renders `flatten()`'s field errors as "Field: reason" clauses, one per
 * offending field. Returns null when the payload carries none, so callers
 * fall through to the plain message.
 */
function fieldMessages(
  data: WorkforceErrorBody | undefined,
  labels: Record<string, string>,
): string | null {
  const details = data?.details;
  if (!details) return null;

  const parts: string[] = [];
  for (const [field, messages] of Object.entries(details.fieldErrors ?? {})) {
    const reason = messages?.[0];
    if (!reason) continue;
    parts.push(`${labels[field] ?? field}: ${lowerFirst(reason)}`);
  }
  // Cross-field refinements (Zod's `.refine` on the object) land here with no
  // field attached — they are still the actual reason for the 400.
  for (const reason of details.formErrors ?? []) {
    if (reason) parts.push(reason);
  }
  return parts.length > 0 ? parts.join("; ") : null;
}

/** Zod messages start capitalised ("Number must be…"); after a "Field: "
 * prefix that reads as a new sentence mid-clause. */
function lowerFirst(text: string): string {
  return text.length > 0 ? text[0].toLowerCase() + text.slice(1) : text;
}
